import { useContext, useCallback, createContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  apiGetValidationSchema,
  type ValidationSchema,
  type FieldRule,
  type FieldCondition,
  type ItemPath,
  type MarketFieldRule,
} from 'api/quote-marketplace';
import { isValidPostalCode } from '../utils/postalCode';

interface FieldValidationError {
  field: string;
  message: string;
  tab: string;
  section: string;
}

interface UseValidationSchemaReturn {
  schema: ValidationSchema | undefined;
  isLoading: boolean;
  error: Error | null;
  isFieldRequired: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => boolean;
  isFieldVisible: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => boolean;
  validateField: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => string | null;
  validateEntity: (
    itemPath: ItemPath,
    entity: Record<string, unknown>,
  ) => FieldValidationError[];
  getFieldRule: (
    itemPath: ItemPath,
    fieldPath: string,
  ) => FieldRule | undefined;
  getMarketFieldRule: (
    itemPath: ItemPath,
    fieldPath: string,
  ) => MarketFieldRule | undefined;
  isFieldRequiredByMarket: (
    itemPath: ItemPath,
    fieldPath: string,
    entity?: Record<string, unknown>,
  ) => boolean;
  getMarketsRequiringField: (
    itemPath: ItemPath,
    fieldPath: string,
    entity?: Record<string, unknown>,
  ) => string[] | null;
}

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (!obj || !path) return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function evaluateRequired(
  required: boolean | FieldCondition,
  entity: Record<string, unknown>,
): boolean {
  if (typeof required === 'boolean') return required;

  const { condition, field, operator, value, fields, conditions } = required;

  if (condition === 'always') return true;

  if (condition === 'when' && field) {
    const actualValue = getNestedValue(entity, field);

    switch (operator) {
      case 'equals':
        return actualValue === value;
      case 'not_equals':
        return actualValue !== value;
      case 'in':
        return Array.isArray(value) && value.includes(actualValue);
      case 'not_in':
        return Array.isArray(value) && !value.includes(actualValue);
      case 'not_empty':
        return hasValue(actualValue);
      case 'greater_than':
        return (
          typeof actualValue === 'number' &&
          typeof value === 'number' &&
          actualValue > value
        );
      default:
        return false;
    }
  }

  if (condition === 'any_of' && fields) {
    return !fields.some((f) => hasValue(getNestedValue(entity, f)));
  }

  if (condition === 'when_any_of' && fields) {
    return fields.some((f) => {
      const actualValue = getNestedValue(entity, f);
      switch (operator) {
        case 'equals':
          return actualValue === value;
        case 'not_equals':
          return actualValue !== value;
        case 'in':
          return Array.isArray(value) && value.includes(actualValue);
        case 'not_in':
          return Array.isArray(value) && !value.includes(actualValue);
        case 'not_empty':
          return hasValue(actualValue);
        default:
          return false;
      }
    });
  }

  if (condition === 'when_item_enabled') return true;

  if (condition === 'when_and' && conditions) {
    return conditions.every((c) => evaluateRequired(c, entity));
  }

  if (condition === 'when_or' && conditions) {
    return conditions.some((c) => evaluateRequired(c, entity));
  }

  return false;
}

function validateFormat(value: unknown, formatType: string): boolean {
  if (!hasValue(value)) return true;

  const strValue = String(value).trim();

  switch (formatType) {
    case 'canadian_postal_code':
      return isValidPostalCode(strValue);
    case 'date':
      return DATE_REGEX.test(strValue);
    case 'email':
      return strValue.includes('@') && strValue.split('@')[1]?.includes('.');
    default:
      return true;
  }
}

interface ValidationSchemaContextValue {
  schema: ValidationSchema | undefined;
  isLoading: boolean;
  error: Error | null;
  isFieldRequired: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => boolean;
  isFieldVisible: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => boolean;
  validateField: (
    itemPath: ItemPath,
    fieldPath: string,
    entity: Record<string, unknown>,
  ) => string | null;
  validateEntity: (
    itemPath: ItemPath,
    entity: Record<string, unknown>,
  ) => FieldValidationError[];
  getFieldRule: (
    itemPath: ItemPath,
    fieldPath: string,
  ) => FieldRule | undefined;
  getMarketFieldRule: (
    itemPath: ItemPath,
    fieldPath: string,
  ) => MarketFieldRule | undefined;
  isFieldRequiredByMarket: (
    itemPath: ItemPath,
    fieldPath: string,
    entity?: Record<string, unknown>,
  ) => boolean;
  getMarketsRequiringField: (
    itemPath: ItemPath,
    fieldPath: string,
    entity?: Record<string, unknown>,
  ) => string[] | null;
}

export const ValidationSchemaContext =
  createContext<ValidationSchemaContextValue | null>(null);

export const useValidationSchema = (
  quoteId?: number,
): UseValidationSchemaReturn => {
  const contextValue = useContext(ValidationSchemaContext);

  const { data, isLoading, error } = useQuery({
    queryKey: ['quote-marketplace', 'validation-schema', quoteId],
    queryFn: () => apiGetValidationSchema(quoteId!),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    enabled: !contextValue && !!quoteId,
  });

  const schemaData = contextValue?.schema ?? data;

  const getFieldRule = useCallback(
    (itemPath: ItemPath, fieldPath: string): FieldRule | undefined => {
      return schemaData?.fields?.[itemPath]?.[fieldPath];
    },
    [schemaData],
  );

  const isFieldRequired = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): boolean => {
      const rule = schemaData?.fields?.[itemPath]?.[fieldPath];
      if (!rule) return false;
      return evaluateRequired(rule.required, entity);
    },
    [schemaData],
  );

  const isFieldVisible = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): boolean => {
      const rule = schemaData?.fields?.[itemPath]?.[fieldPath];
      if (!rule?.visible_when) return true;

      const { field, operator, values } = rule.visible_when;
      const actualValue = getNestedValue(entity, field);

      if (operator === 'in') {
        if (Array.isArray(actualValue)) {
          return actualValue.some((v) => values.includes(String(v)));
        }
        return values.includes(String(actualValue));
      }

      if (operator === 'not_in') {
        if (Array.isArray(actualValue)) {
          return !actualValue.some((v) => values.includes(String(v)));
        }
        return !values.includes(String(actualValue));
      }

      return true;
    },
    [schemaData],
  );

  const validateField = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): string | null => {
      const rule = schemaData?.fields?.[itemPath]?.[fieldPath];
      if (!rule) return null;

      const value = getNestedValue(entity, fieldPath);
      const isRequired = evaluateRequired(rule.required, entity);

      if (isRequired && !hasValue(value)) {
        if (typeof rule.required === 'object' && rule.required.message) {
          return rule.required.message;
        }
        return rule.message;
      }

      if (hasValue(value)) {
        if (rule.format && !validateFormat(value, rule.format)) {
          return rule.format_message ?? `Invalid format`;
        }

        if (
          rule.min_length !== undefined &&
          typeof value === 'string' &&
          value.trim().length < rule.min_length
        ) {
          return rule.message;
        }

        if (rule.min_value !== undefined && isRequired) {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : (value as number);
          if (!isNaN(numValue) && numValue < rule.min_value) {
            return rule.message;
          }
        }
      }

      return null;
    },
    [schemaData],
  );

  const validateEntity = useCallback(
    (
      itemPath: ItemPath,
      entity: Record<string, unknown>,
    ): FieldValidationError[] => {
      const rules = schemaData?.fields?.[itemPath];
      const marketRules = schemaData?.market_fields?.[itemPath];
      const errors: FieldValidationError[] = [];

      if (rules) {
        for (const [fieldPath, rule] of Object.entries(rules)) {
          const errorMessage = validateField(itemPath, fieldPath, entity);
          if (errorMessage) {
            errors.push({
              field: fieldPath,
              message: errorMessage,
              tab: rule.tab,
              section: rule.section,
            });
          }
        }
      }

      if (marketRules) {
        for (const [fieldPath, marketRule] of Object.entries(marketRules)) {
          if (rules?.[fieldPath]) continue;

          if (marketRule.required_by.length === 0) continue;

          if (marketRule.follows) {
            const triggerValue = entity[marketRule.follows.field];
            if (!triggerValue) continue;
            if (!marketRule.follows.values.includes(String(triggerValue))) {
              continue;
            }
          }

          const value = getNestedValue(entity, fieldPath);
          if (!hasValue(value)) {
            errors.push({
              field: fieldPath,
              message: marketRule.message,
              tab: 'additional',
              section: 'market',
            });
          }
        }
      }

      return errors;
    },
    [schemaData, validateField],
  );

  const getMarketFieldRule = useCallback(
    (itemPath: ItemPath, fieldPath: string): MarketFieldRule | undefined => {
      return schemaData?.market_fields?.[itemPath]?.[fieldPath];
    },
    [schemaData],
  );

  const isFieldRequiredByMarket = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity?: Record<string, unknown>,
    ): boolean => {
      const marketRule = schemaData?.market_fields?.[itemPath]?.[fieldPath];
      if (!marketRule || marketRule.required_by.length === 0) return false;

      if (marketRule.follows && entity) {
        const triggerValue = getNestedValue(entity, marketRule.follows.field);
        if (triggerValue === undefined || triggerValue === null) return false;
        const valuesToCheck = marketRule.follows.values.map((v: unknown) =>
          typeof v === 'boolean' ? v : String(v),
        );
        const normalizedTrigger =
          typeof triggerValue === 'boolean'
            ? triggerValue
            : String(triggerValue);
        if (!valuesToCheck.includes(normalizedTrigger)) {
          return false;
        }
      }

      return true;
    },
    [schemaData],
  );

  const getMarketsRequiringField = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity?: Record<string, unknown>,
    ): string[] | null => {
      const marketRule = schemaData?.market_fields?.[itemPath]?.[fieldPath];
      if (!marketRule || marketRule.display_names.length === 0) return null;

      if (marketRule.follows && entity) {
        const triggerValue = getNestedValue(entity, marketRule.follows.field);
        if (triggerValue === undefined || triggerValue === null) return null;
        const valuesToCheck = marketRule.follows.values.map((v: unknown) =>
          typeof v === 'boolean' ? v : String(v),
        );
        const normalizedTrigger =
          typeof triggerValue === 'boolean'
            ? triggerValue
            : String(triggerValue);
        if (!valuesToCheck.includes(normalizedTrigger)) {
          return null;
        }
      }

      return marketRule.display_names;
    },
    [schemaData],
  );

  if (contextValue) {
    return contextValue;
  }

  return {
    schema: data,
    isLoading,
    error,
    isFieldRequired,
    isFieldVisible,
    validateField,
    validateEntity,
    getFieldRule,
    getMarketFieldRule,
    isFieldRequiredByMarket,
    getMarketsRequiringField,
  };
};

export { getNestedValue, hasValue, evaluateRequired, validateFormat };
